import React, { useState, useRef, useEffect } from "react";
import { BsThreeDots } from "react-icons/bs";
import { createPortal } from "react-dom";
import "./OptionsMenu.scss";

interface Option {
    label: string;
    action: (row: any) => void;
    icon?: React.ReactNode;
    enabled?: boolean;
}

interface OptionsMenuProps {
    options: Option[];
    row: any;
}

const OptionsMenu: React.FC<OptionsMenuProps> = ({ options, row }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleToggle = () => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const scrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
            const scrollLeft =
                window.pageXOffset || document.documentElement.scrollLeft;

            // Calcular posición del menú
            const menuWidth = 160; // Ancho aproximado del menú
            const menuHeight =
                options.filter((x) => x.enabled || x.enabled == null).length *
                44; // Altura aproximada

            let top = rect.bottom + scrollTop;
            let left = rect.right + scrollLeft - menuWidth;

            // Ajustar si se sale por la derecha
            if (left < scrollLeft + 10) {
                left = rect.left + scrollLeft;
            }

            // Ajustar si se sale por abajo
            if (top + menuHeight > window.innerHeight + scrollTop) {
                top = rect.top + scrollTop - menuHeight;
            }

            setMenuPosition({ top, left });
        }
        setIsOpen(!isOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (
            buttonRef.current &&
            !buttonRef.current.contains(event.target as Node)
        ) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    const handleOptionClick = (option: Option) => {
        option.action(row);
        setIsOpen(false);
    };

    const renderMenu = () => {
        if (!isOpen) return null;

        const menuContent = (
            <div
                className="options-menu__content options-menu__content--portal"
                style={{
                    position: "absolute",
                    top: `${menuPosition.top}px`,
                    left: `${menuPosition.left}px`,
                    zIndex: 9999,
                }}
            >
                {options
                    .filter((x) => x.enabled || x.enabled == null)
                    .map((option, index) => (
                        <div
                            key={index}
                            className="options-menu__content__item"
                            onClick={() => handleOptionClick(option)}
                        >
                            <div className="options-menu__content__item__icon">
                                {option.icon}
                            </div>
                            <span className="options-menu__content__item__label">
                                {option.label}
                            </span>
                        </div>
                    ))}
            </div>
        );

        return createPortal(menuContent, document.body);
    };

    return (
        <div className="options-menu">
            <button
                ref={buttonRef}
                className="options-menu__button"
                onClick={handleToggle}
            >
                {(BsThreeDots as any)({
                    className: "options-menu__button__icon",
                })}
            </button>
            {renderMenu()}
        </div>
    );
};

export default OptionsMenu;
